import { useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Card, CardContent } from "@/components/ui/card";
import { Landmark, X } from "lucide-react";
import { ExportButtons } from "@/components/ExportButtons";
import { inr, inrShort } from "@/pages/Dashboard";

const BUCKET_TONE = { "Not Due": "text-emerald-600", "0-30": "text-sky-600", "31-60": "text-amber-600", "61-90": "text-orange-600", "91-180": "text-red-600", "180+": "text-red-700" };

const FinancierRows = ({ summary, selected, onSelect, testPrefix }) => (
  <table className="text-xs tabular-nums min-w-[420px]" data-testid={`${testPrefix}-financing-table`}>
    <thead>
      <tr className="text-slate-500 uppercase tracking-wider text-[10px]">
        <th className="text-left pb-1 pr-4">Financier</th>
        <th className="text-right pb-1 pr-4">Open</th>
        <th className="text-right pb-1 pr-4">Docs</th>
        <th className="text-right pb-1">Oldest</th>
      </tr>
    </thead>
    <tbody>
      {summary.financiers.map((f) => {
        const active = selected?.includes(f.code);
        return (
          <tr key={f.code} onClick={() => onSelect?.(active ? [] : [f.code])} title="Click to filter the drill-down to this financier"
            className={`border-t border-slate-100 cursor-pointer transition-colors ${active ? "bg-violet-50" : "hover:bg-slate-50"}`} data-testid={`${testPrefix}-financier-row-${f.code}`}>
            <td className="py-1.5 pr-4 font-semibold text-slate-800 max-w-[220px] truncate">{f.name}<span className="ml-1 font-mono font-normal text-slate-400">{f.code}</span></td>
            <td className="py-1.5 pr-4 text-right font-bold text-violet-700">{inrShort(f.total)}</td>
            <td className="py-1.5 pr-4 text-right text-slate-600">{f.count}</td>
            <td className={`py-1.5 text-right font-semibold ${BUCKET_TONE[f.oldest_bucket] || "text-slate-500"}`}>{f.oldest_bucket || "—"}</td>
          </tr>
        );
      })}
    </tbody>
  </table>
);

const MaturityGrid = ({ summary, finMonth, onSelectMonth, testPrefix }) => {
  const cols = summary.maturity_columns;
  const chart = cols.map((c) => ({ key: c.key, label: c.label.replace(" (this month)", "*"), total: summary.maturity_totals[c.key] }));
  const pick = (k) => onSelectMonth?.(finMonth === k ? "" : k);
  return (
    <div className="w-full space-y-3" data-testid={`${testPrefix}-maturity-view`}>
      <div className="h-36">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chart} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: "#64748b" }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={(v) => inrShort(v)} tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={64} />
            <Tooltip formatter={(v) => inr(v)} contentStyle={{ fontSize: 12, borderRadius: 6 }} cursor={{ fill: "rgba(139,92,246,0.08)" }} />
            <Bar dataKey="total" radius={[4, 4, 0, 0]} onClick={(d) => pick(d.key)} className="cursor-pointer">
              {chart.map((d) => <Cell key={d.key} fill={d.key === "overdue" ? "#dc2626" : finMonth === d.key ? "#5b21b6" : finMonth ? "#c4b5fd" : "#7c3aed"} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="overflow-x-auto">
        <table className="text-xs tabular-nums w-full" data-testid={`${testPrefix}-maturity-table`}>
          <thead>
            <tr className="text-slate-500 uppercase tracking-wider text-[10px]">
              <th className="text-left pb-1 pr-3 sticky left-0 bg-white">Financier</th>
              {cols.map((c) => (
                <th key={c.key} onClick={() => pick(c.key)} data-testid={`${testPrefix}-maturity-col-${c.key}`}
                  className={`text-right pb-1 px-2 cursor-pointer whitespace-nowrap rounded ${finMonth === c.key ? "bg-violet-100 text-violet-800" : "hover:text-violet-700"}`} title="Click to filter the drill-down to this maturity month">
                  {c.label.replace(" (this month)", "")}{c.label.includes("this month") && <span className="text-violet-500">*</span>}
                </th>
              ))}
              <th className="text-right pb-1 pl-3">Total</th>
            </tr>
          </thead>
          <tbody>
            {summary.financiers.map((f) => (
              <tr key={f.code} className="border-t border-slate-100" data-testid={`${testPrefix}-maturity-row-${f.code}`}>
                <td className="py-1.5 pr-3 font-semibold text-slate-800 max-w-[200px] truncate sticky left-0 bg-white">{f.name}</td>
                {cols.map((c) => {
                  const v = f.maturity[c.key];
                  return (
                    <td key={c.key} onClick={() => v && pick(c.key)} className={`py-1.5 px-2 text-right ${v ? "cursor-pointer" : ""} ${finMonth === c.key ? "bg-violet-50" : ""} ${c.key === "overdue" && v ? "text-red-600 font-semibold" : "text-slate-700"}`}>
                      {v ? inrShort(v) : <span className="text-slate-300">—</span>}
                    </td>
                  );
                })}
                <td className="py-1.5 pl-3 text-right font-bold text-violet-700">{inrShort(f.total)}</td>
              </tr>
            ))}
            <tr className="border-t-2 border-slate-200 font-bold" data-testid={`${testPrefix}-maturity-totals`}>
              <td className="py-1.5 pr-3 text-slate-800 sticky left-0 bg-white">Cash need</td>
              {cols.map((c) => <td key={c.key} className={`py-1.5 px-2 text-right ${c.key === "overdue" ? "text-red-700" : "text-slate-900"} ${finMonth === c.key ? "bg-violet-50" : ""}`}>{summary.maturity_totals[c.key] ? inrShort(summary.maturity_totals[c.key]) : "—"}</td>)}
              <td className="py-1.5 pl-3 text-right text-violet-800">{inrShort(summary.financier_total)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-slate-400">* current month as on {summary.as_of}. Overdue = due date before the as-on date. Click a month to narrow the drill-down below to those maturities.</p>
    </div>
  );
};

export const FinancingSummary = ({ summary, selected, onSelect, finMonth, onSelectMonth, exportParams, testPrefix = "ap-ageing" }) => {
  const [view, setView] = useState("financier");
  if (!summary || (!summary.financiers?.length && !finMonth)) return null;
  const grand = summary.financier_total + summary.regular_total;
  const pct = Math.max(0, Math.min(100, summary.share_pct));
  const monthLabel = finMonth && (summary.maturity_columns.find((c) => c.key === finMonth)?.label || finMonth);
  const { fin_month: _omit, ...matParams } = exportParams || {};
  return (
    <Card className="border-violet-200 shadow-sm fade-in-up" data-testid={`${testPrefix}-financing-card`}>
      <CardContent className="p-5 space-y-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-violet-50 flex items-center justify-center shrink-0"><Landmark className="h-5 w-5 text-violet-600" /></div>
            <div>
              <p className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">Funded exposure — Financier payables vs regular supplier dues</p>
              <p className="text-[11px] text-slate-400">SG Finserve / TReDS / LC / buyer-credit vendors (auto-detected + Admin → SAP Settings list) · follows the filters above</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {finMonth && (
              <button type="button" onClick={() => onSelectMonth?.("")} className="inline-flex items-center gap-1 rounded-full bg-violet-100 text-violet-800 px-2.5 py-1 text-xs font-semibold" data-testid={`${testPrefix}-maturity-filter-chip`}>
                Maturity: {monthLabel} <X className="h-3 w-3" />
              </button>
            )}
            <div className="inline-flex rounded-md border border-slate-200 p-0.5 bg-slate-50 text-xs" data-testid={`${testPrefix}-financing-view-toggle`}>
              {[["financier", "By financier"], ["maturity", "By maturity"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setView(v)} data-testid={`${testPrefix}-financing-view-${v}`}
                  className={`px-2.5 py-1 rounded font-medium transition-colors ${view === v ? "bg-white shadow-sm text-violet-700" : "text-slate-500 hover:text-slate-700"}`}>{l}</button>
              ))}
            </div>
            {view === "maturity" && <ExportButtons report="financier-maturity" endpoint="/reports/ap-ageing/financing-maturity/export" params={matParams} />}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-4 max-w-2xl">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400">Financier payables</p>
            <p className="text-xl font-black tracking-tight text-violet-700" data-testid={`${testPrefix}-financing-total`}>{inrShort(summary.financier_total)}</p>
            <p className="text-[11px] text-slate-400">{summary.financier_count} docs · {summary.share_pct}% of open AP</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400">Regular supplier dues</p>
            <p className="text-xl font-black tracking-tight text-slate-800" data-testid={`${testPrefix}-financing-regular`}>{inrShort(summary.regular_total)}</p>
            <p className="text-[11px] text-slate-400">{(100 - summary.share_pct).toFixed(1)}% of open AP</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400">Total open AP</p>
            <p className="text-xl font-black tracking-tight text-slate-900">{inrShort(grand)}</p>
            <div className="h-1.5 rounded-full bg-slate-200 mt-2 overflow-hidden"><div className="h-full bg-violet-500" style={{ width: `${pct}%` }} /></div>
          </div>
        </div>
        {view === "financier"
          ? <FinancierRows summary={summary} selected={selected} onSelect={onSelect} testPrefix={testPrefix} />
          : <MaturityGrid summary={summary} finMonth={finMonth} onSelectMonth={onSelectMonth} testPrefix={testPrefix} />}
      </CardContent>
    </Card>
  );
};

export const FundedExposureLine = ({ financing }) => {
  if (!financing || !financing.financier_total) return null;
  return (
    <Card className="border-violet-200 shadow-sm fade-in-up" data-testid="dashboard-financing-card">
      <CardContent className="p-4 flex flex-wrap items-center gap-x-6 gap-y-2">
        <div className="flex items-center gap-2.5">
          <Landmark className="h-4 w-4 text-violet-600" />
          <span className="text-xs font-semibold tracking-[0.05em] uppercase text-slate-500">Funded AP exposure</span>
        </div>
        <span className="text-sm text-slate-700">Financier payables <strong className="text-violet-700" data-testid="dashboard-financing-total">{inrShort(financing.financier_total)}</strong> <span className="text-slate-400">({financing.share_pct}%)</span></span>
        <span className="text-sm text-slate-700">Regular supplier dues <strong className="text-slate-900">{inrShort(financing.regular_total)}</strong></span>
        <span className="text-[11px] text-slate-400 ml-auto">{financing.financiers.length} financier{financing.financiers.length === 1 ? "" : "s"}: {financing.financiers.slice(0, 4).map((f) => f.name.split(/\s{2,}|\(/)[0].trim()).join(", ")}{financing.financiers.length > 4 ? "…" : ""}</span>
      </CardContent>
    </Card>
  );
};
